import * as _ from 'lodash';
import type { FunctionComponent, ReactNode } from 'react';
import Icon from '../Icon';
import type { IconClassName } from '../Icon';
import ListRowDetailsItemBadge from './ListRowDetailsItemBadge';

type Props = {
  leadingSlot?: ReactNode;
  trailingSlot?: ReactNode;
};

/**
 * ListRowDetailsItem represents a single "item" of a list row detail.
 * It is made up of some content, with an optional leading and trailing
 * content "slots". This allows you to build whatever sort of detail you need.
 */
const ListRowDetailsItem: FunctionComponent<Props> = ({ children, leadingSlot, trailingSlot }) => (
  <div className="fossa-list__row__details__item">
    {leadingSlot && (
      <span className="fossa-list__row__details__item__leading">
        {leadingSlot}
      </span>
    )}
    <span className="fossa-list__row__details__content">
      {children}
    </span>
    {trailingSlot && (
      <span className="fossa-list__row__details__item__trailing">
        {trailingSlot}
      </span>
    )}
  </div>
);

type IconBadgeListRowDetailsItemProps = {
  //  If present, this will display an icon at the leading side of the item.
  icon?: IconClassName;
  //  If present, this will display a badge with the count in it. `0` will be displayed, so use `null`/don't pass it if you don't want the badge to render.
  count?: number | null;
};

/**
 * Common pattern for our ListRowDetailsItem is for them to contain
 * an Icon and a count (ie: `<Icon icon="projects" /> Projects (8)`).
 */
export const IconBadgeListRowDetailsItem: FunctionComponent<IconBadgeListRowDetailsItemProps> = ({
  children,
  icon,
  count,
}) => (
  <ListRowDetailsItem
    leadingSlot={icon && (
      <span className="fossa-list__row__details__item__leading__icon">
        <Icon icon={icon} />
      </span>
    )}
    trailingSlot={_.isNumber(count) && (
      <ListRowDetailsItemBadge>
        {count}
      </ListRowDetailsItemBadge>
    )}
  >
    {children}
  </ListRowDetailsItem>
);

export default ListRowDetailsItem;
